import {
  BeatResolution,
  getPrimaryInstrumentFromMedia,
  MAX_LOOP_COUNT,
  SampleCategories,
  StemType
} from '@arpeggi/shared'
import { Instance, types } from 'mobx-state-tree'
import { Key } from 'react'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { ForDirectExtend } from '../../../../features/mobx'
import { InstrumentFlow, InstrumentPart, Octave } from '../../../client/genesis/genesis_proto_pb'
import { SongGenerator } from '../../../generator/SongGenerator'
import { LoopRefStore } from '../loop/LoopRefStore'
import { MelodicLoopStore } from '../loop/MelodicLoopStore'
import { getRandomSampleForCategory } from '../sample/SampleUtils'
import { ISampleWithEffectsStore, SampleWithEffectsStore } from '../sample/SampleWithEffectsStore'
import { StemSettingsStore } from '../StemSettingsStore'
import { StemStore } from '../StemStore'

// Represents a single stem.
export const InstrumentStemStore = StemStore.named(StemType.INSTRUMENT)
  .props({
    melodicSample: types.maybe(SampleWithEffectsStore),
    stemType: StemType.INSTRUMENT
  })
  .views((self) => ({
    // Returns null if no loop at this section
    getMelodicSample(): number {
      return self.melodicSample.sampleIndex
    },
    getCurrentSample(): ISampleWithEffectsStore {
      return self.melodicSample
    }
  }))
  .actions((self) => {
    function init(songDisplayLength: number, sample: ISampleWithEffectsStore) {
      self.stemSettings = StemSettingsStore.create()
      self.stemSettings.initialize(songDisplayLength)
      self.stemSettings.setStemGain(0.5)
      const newSampleWithEffects = SampleWithEffectsStore.create()
      newSampleWithEffects.cloneFrom(sample)
      self.melodicSample = newSampleWithEffects
    }
    function updateSampleStart(startTime: number, index: number | undefined = undefined) {
      self.melodicSample.updateSampleStart(startTime)
    }
    function updateSampleEnd(endTime: number, index: number | undefined = undefined) {
      self.melodicSample.updateSampleEnd(endTime)
    }

    function toggleEnvelope() {
      self.melodicSample.toggleEnvelope()
    }

    function addLoop() {
      if (self.loops.length >= MAX_LOOP_COUNT) return
      const loopRef = LoopRefStore.create()
      self.loops.push(loopRef)
      const melodicLoop = MelodicLoopStore.create()
      melodicLoop.initialize(BeatResolution.EIGHTH_NOTE)
      loopRef.setMelodicLoop(melodicLoop)
    }

    function updateAttack(attack: number) {
      self.melodicSample.adsr.attack = attack
    }

    function updateDecay(decay: number) {
      self.melodicSample.adsr.decay = decay
    }
    function updateSustain(sustain: number) {
      self.melodicSample.adsr.sustain = sustain
    }
    function updateRelease(release: number) {
      self.melodicSample.adsr.release = release
    }
    // TODO we currently have two different ways to update sample.
    // Decide whether we only ever want to update sample with effects or sample.
    function updateMelodicSample(sample: ISampleWithEffectsStore) {
      self.melodicSample = SampleWithEffectsStore.create()
      self.melodicSample.cloneFrom(sample)
    }
    function randomizeMelodicSample() {
      const currentSample = ArpRegistry.retrieveFromCache(self.melodicSample.sampleIndex)
      if (!currentSample) return
      const sampleWithEffects = getRandomSampleForCategory(
        getPrimaryInstrumentFromMedia(currentSample)
      )

      // Register it for undo
      updateMelodicSample(sampleWithEffects)
    }
    function randomize(generator: SongGenerator) {
      randomizeMelodicSample()
      randomizeLoop(generator, null)
    }

    // Randomizes a specific loop
    function randomizeLoop(generator: SongGenerator, loopNumber: number) {
      const category = ArpRegistry.retrieveFromCache(self.melodicSample.sampleIndex)?.category
      if (SampleCategories.Bass.includes(category)) {
        loopNumber === null
          ? self.loops.forEach((loop) => {
              generator.getMelodicGenerator().generateBassLoop(loop.melodicLoop)
            })
          : generator.getMelodicGenerator().generateBassLoop(self.loops[loopNumber].melodicLoop)
      } else {
        loopNumber === null
          ? self.loops.forEach((loop) => {
              generator.getMelodicGenerator().generateLeadLoop(loop.melodicLoop)
            })
          : generator.getMelodicGenerator().generateLeadLoop(self.loops[loopNumber].melodicLoop)
      }
    }

    function clearLoop(loopNumber: number) {
      const loopRef = LoopRefStore.create()
      const melodicLoop = MelodicLoopStore.create()
      melodicLoop.initialize(BeatResolution.EIGHTH_NOTE)
      loopRef.setMelodicLoop(melodicLoop)
      self.loops[loopNumber] = loopRef
    }

    function fromGenesisMelodic(
      sample: ISampleWithEffectsStore,
      flowList: InstrumentFlow.Part[],
      partA: InstrumentPart,
      partB: InstrumentPart,
      partC: InstrumentPart,
      gain: number,
      octave: Octave,
      autoRelease: boolean,
      resolution: BeatResolution,
      genesisKey: Key
    ) {
      // TODO use a mapping to find similar samples from genesis.
      self.melodicSample = sample as any
      self.melodicSample.updateAutoRelease(autoRelease)

      self.stemSettings = StemSettingsStore.create()
      self.stemSettings.initialize(flowList.length)
      self.stemSettings.setStemGain(gain / 15)

      // Add the correct number of loops.
      // If a part is used in the song, assign it to the next available loop.
      let nextLoop = 0
      const parts = [
        { flowPart: InstrumentFlow.Part.A, part: partA },
        { flowPart: InstrumentFlow.Part.B, part: partB },
        { flowPart: InstrumentFlow.Part.C, part: partC }
      ]
      parts.forEach(({ flowPart, part }) => {
        if (flowList.includes(flowPart)) {
          const melodicLoop = MelodicLoopStore.create()
          melodicLoop.initialize(resolution)
          melodicLoop.fromGenesis(part, resolution, octave, 0) // note, we don't use genesis key, so things are all in C.
          const loopRefStore = LoopRefStore.create()
          loopRefStore.setMelodicLoop(melodicLoop)
          self.loops.push(loopRefStore)
          for (let i = 0; i < flowList.length; i++) {
            if (flowList[i] === flowPart) {
              self.stemSettings.setArrangement(i, nextLoop + 1)
            }
          }
          nextLoop++
        }
      })
    }
    return {
      init,
      addLoop,
      toggleEnvelope,
      updateSampleStart,
      updateSampleEnd,
      updateAttack,
      updateDecay,
      updateSustain,
      updateRelease,
      updateMelodicSample,
      randomizeMelodicSample,
      fromGenesisMelodic,
      randomize,
      randomizeLoop,
      clearLoop
    }
  })

export interface IInstrumentStemStore
  extends ForDirectExtend<Instance<typeof InstrumentStemStore>> {}
